/* Add-to-cart flight: a count chip ("1") flies one smooth arc (a quadratic Bezier, i.e. a
   parabola) from the add button into the header bag,
   then the bag gives a little bump. Transform + opacity only (Web Animations API, no deps).
   Reduced motion: no travel, the chip just fades in and out on the bag.
   API: window.saysoFlyToBag(source, count = 1) -> Promise. source is the clicked element, or
   better an anchor from window.saysoFlyAnchor(element) taken AT CLICK TIME: callers that hide
   the button before the add finishes (the builder's "in your cart" state) need it, because the
   chip then starts where the button was: its click-time offset inside the nearest ancestor that
   is still on screen (so scrolling, scroll anchoring and layout shifts above don't move it) (resolves when it has landed,
   or straight away if there is nothing to fly from/to). */
(() => {
  const DURATION = 600;
  const FRAMES = 90; // samples along the curve; linear between them, so no visible corners

  // Time easing applied along the curve: leaves the button at speed, settles into the bag
  // (ease-out cubic; no stall at the start).
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  // One parabola from start to end, bowed evenly: the control point sits off the middle of the
  // straight line, on its upper side, so the curve bends the same way the whole trip (no flat
  // run then a hook). Bow shrinks if it would leave the screen.
  const BOW = 0.22; // control offset as a share of the trip length
  const arcKeyframes = (start, end) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const length = Math.hypot(dx, dy) || 1;
    let nx = dy / length;
    let ny = -dx / length;
    if (ny > 0 || (ny === 0 && nx > 0)) { nx = -nx; ny = -ny; } // the upper/left side
    const point = (t, c) => {
      const u = 1 - t;
      return { x: u * u * start.x + 2 * u * t * c.x + t * t * end.x, y: u * u * start.y + 2 * u * t * c.y + t * t * end.y };
    };
    const margin = 24;
    let bow = BOW * length;
    let control;
    for (let tries = 0; tries < 6; tries++, bow *= 0.6) {
      control = { x: (start.x + end.x) / 2 + nx * bow, y: (start.y + end.y) / 2 + ny * bow };
      const apex = point(0.5, control); // farthest point from the line
      if (apex.x >= margin && apex.x <= window.innerWidth - margin && apex.y >= margin) break;
    }
    return Array.from({ length: FRAMES + 1 }, (_, i) => {
      const { x, y } = point(easeOutCubic(i / FRAMES), control);
      return { transform: `translate(${x.toFixed(3)}px, ${y.toFixed(3)}px)`, offset: i / FRAMES };
    });
  };
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const visible = (el) => {
    if (!(el instanceof Element) || !el.isConnected) return null;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 ? r : null;
  };

  const headerBag = () =>
    [...document.querySelectorAll(".site-header .bag-link, .bag-link")].find(visible) || null;

  // Inside a fixed/sticky box the button doesn't move with the page, so no scroll correction.
  const pinned = (el) => {
    for (let n = el; n instanceof Element; n = n.parentElement) {
      const { position } = getComputedStyle(n);
      if (position === "fixed" || position === "sticky") return true;
    }
    return false;
  };

  // Snapshot of the clicked button: its rect, its offset inside each ancestor, and the scroll.
  const anchor = (el) => {
    const rect = visible(el);
    if (!rect) return null;
    const frames = [];
    for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
      const r = visible(n);
      if (r) frames.push({ node: n, dx: rect.left - r.left, dy: rect.top - r.top });
    }
    return { el, rect, frames, scrollX: window.scrollX, scrollY: window.scrollY, pinned: pinned(el) };
  };

  // Where the chip starts, in current viewport coords.
  const startRect = (source) => {
    if (source instanceof Element) return visible(source);
    if (!source?.rect) return null;
    const live = visible(source.el); // still on screen: its own rect now (handles transforms/sticky)
    if (live) return live;
    const { rect } = source;
    // Hidden/swapped: same spot inside the nearest ancestor that is still shown.
    for (const { node, dx, dy } of source.frames || []) {
      const r = visible(node);
      if (r) return { left: r.left + dx, top: r.top + dy, width: rect.width, height: rect.height };
    }
    const dx = source.pinned ? 0 : source.scrollX - window.scrollX;
    const dy = source.pinned ? 0 : source.scrollY - window.scrollY;
    return { left: rect.left + dx, top: rect.top + dy, width: rect.width, height: rect.height };
  };

  const centre = (r) => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 });

  // The bag count ticks over: sayso.co's counter cue (assets/sayso-sound.js), if loaded.
  const landSound = () => window.saysoSound?.play("count", { theme: "mech", duration: 300 });

  const settle = (anims) =>
    Promise.allSettled(anims.map((a) => a.finished)).then(() => undefined);

  function flyToBag(source, count = 1) {
    const bag = headerBag();
    const bagRect = visible(bag);
    if (!bagRect || typeof document.body.animate !== "function") return Promise.resolve();

    const amount = Math.max(1, Math.round(Number(count) || 1));
    // Land where the count badge sits (bottom-right of the bag), else the bag centre.
    const badgeRect = visible(bag.querySelector(".bag-count"));
    const end = badgeRect ? centre(badgeRect) : { x: bagRect.right - 6, y: bagRect.bottom - 8 };

    // Outer box moves on X, inner chip on Y with a different curve -> an arc up and right.
    const track = document.createElement("span");
    track.className = "sayso-fly";
    track.setAttribute("aria-hidden", "true");
    const chip = document.createElement("span");
    chip.className = "sayso-fly-chip";
    chip.textContent = String(amount);
    track.append(chip);
    document.body.append(track);

    const sourceRect = startRect(source);
    if (reduceMotion.matches || !sourceRect) {
      track.style.transform = `translate(${end.x}px, ${end.y}px)`;
      chip.style.transform = "translate(-50%, -50%)";
      const fade = chip.animate(
        [{ opacity: 0 }, { opacity: 1, offset: 0.3 }, { opacity: 1, offset: 0.7 }, { opacity: 0 }],
        { duration: reduceMotion.matches ? 600 : 400, easing: "linear", fill: "forwards" },
      );
      return settle([fade]).then(() => {
        track.remove();
        landSound();
      });
    }

    const start = centre(sourceRect);
    // Outer box rides the curve; the chip only scales (big on the button, smaller into the bag).
    const path = track.animate(arcKeyframes(start, end), { duration: DURATION, easing: "linear", fill: "forwards" });
    const look = chip.animate(
      [
        { transform: "translate(-50%, -50%) scale(1)", opacity: 0 },
        { transform: "translate(-50%, -50%) scale(1)", opacity: 1, offset: 0.05 },
        { transform: "translate(-50%, -50%) scale(1)", opacity: 1, offset: 0.6 },
        { transform: "translate(-50%, -50%) scale(0.7)", opacity: 1, offset: 0.94 },
        { transform: "translate(-50%, -50%) scale(0.6)", opacity: 0 },
      ],
      { duration: DURATION, easing: "linear", fill: "forwards" },
    );

    return settle([path, look]).then(() => {
      track.remove();
      landSound();
      const icon = bag.querySelector("svg") || bag;
      icon.animate(
        [{ transform: "scale(1)" }, { transform: "scale(1.18)" }, { transform: "scale(1)" }],
        { duration: 260, easing: "cubic-bezier(0.3, 0, 0.2, 1)" },
      );
    });
  }

  window.saysoFlyToBag = flyToBag;
  window.saysoFlyAnchor = anchor;
  // Event hook for other modules: dispatch sayso:added-to-cart with { source, count }.
  document.addEventListener("sayso:added-to-cart", (event) => {
    void flyToBag(event.detail?.source, event.detail?.count);
  });
})();
